import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { useT } from "../i18n";
import { tg, shareInvite } from "../telegram";
import type { InviteResult, Me, UserRow } from "../types";

export default function AdminUsers({ me }: { me: Me }) {
  const { t } = useT();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [asAdmin, setAsAdmin] = useState(false);
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () =>
    api.get<UserRow[]>("/api/users").then(setUsers).catch((e: ApiError) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const alert_ = (msg: string) => {
    const a = tg()?.showAlert;
    if (a) a(msg);
    else window.alert(msg);
  };

  const createInvite = async () => {
    if (creating) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setCreating(true);
    setInvite(null);
    setCopied(false);
    try {
      const inv = await api.post<InviteResult>("/api/invites", {
        full_name: name.trim(),
        role: asAdmin ? "admin" : "reader",
      });
      setInvite(inv);
      setName("");
      setAsAdmin(false);
      load();
    } catch (e) {
      alert_(e instanceof ApiError ? e.message : t("users.invite_fail"));
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
      tg()?.HapticFeedback?.notificationOccurred("success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert_(invite.url);
    }
  };

  const toggle = async (u: UserRow) => {
    if (busyId || u.id === me.id) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setBusyId(u.id);
    try {
      await api.post(`/api/users/${u.id}/toggle`);
      load();
    } catch (e) {
      alert_(e instanceof ApiError ? e.message : t("common.error"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page">
      <header className="app-header">
        <h1 className="app-title">👥 {t("users.title")}</h1>
      </header>

      <div className="card">
        <div className="card-title">{t("users.invite_heading")}</div>
        <input
          className="search"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("users.name_placeholder")}
        />
        <label className="check-row">
          <input type="checkbox" checked={asAdmin} onChange={(e) => setAsAdmin(e.target.checked)} />
          {t("users.make_admin")}
        </label>
        <button className="btn" onClick={createInvite} disabled={creating}>
          {creating ? t("users.creating") : "🔗 " + t("users.create")}
        </button>

        {invite && (
          <div className="invite-result">
            <div className="hint small">{t("users.link_ready")}</div>
            <div className="invite-link">{invite.url}</div>
            <div className="invite-actions">
              <button className="chip" onClick={copy}>
                {copied ? t("users.copied") : "📋 " + t("users.copy")}
              </button>
              <button className="chip" onClick={() => shareInvite(invite.url, t("users.share_text"))}>
                ↗ {t("users.share")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card-title section-gap">{t("users.list_heading")}</div>
      {error && <div className="alert error">{error}</div>}
      {!users && !error && (
        <div className="empty">
          <span className="spinner" />
        </div>
      )}
      {users && users.length === 0 && <div className="empty">{t("users.empty")}</div>}

      <div className="user-list">
        {users?.map((u) => (
          <div key={u.id} className={"user-row" + (u.is_active ? "" : " off")}>
            <div className="user-main">
              <div className="user-name">
                {u.full_name}
                {u.id === me.id && <span className="hint small"> ({t("users.you")})</span>}
              </div>
              <div className="user-meta">
                <span className={"badge" + (u.role === "admin" ? " admin" : "")}>
                  {t(u.role === "admin" ? "users.role.admin" : "users.role.reader")}
                </span>
                <span className="hint small">{t(u.is_active ? "users.active" : "users.inactive")}</span>
              </div>
            </div>
            {u.id !== me.id && (
              <button
                className={"chip" + (u.is_active ? " danger" : "")}
                disabled={busyId === u.id}
                onClick={() => toggle(u)}
              >
                {t(u.is_active ? "users.deactivate" : "users.activate")}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
